import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatCNPJ, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, CloudArrowDown, PencilSimple, Trash, UploadSimple } from "@phosphor-icons/react";

const empty = {
  cnpj: "", razao_social: "", nome_fantasia: "", ie: "", suframa: "",
  vendedor: "", vendedor_id: "", vendedor_email: "", vendedor_codigo: "",
  endereco: { logradouro: "", numero: "", complemento: "", bairro: "", municipio: "", uf: "", cep: "" },
  endereco_entrega: { logradouro: "", numero: "", complemento: "", bairro: "", municipio: "", uf: "", cep: "" },
  usa_endereco_entrega: false, contato: "", email: "", celular: "", telefone: "",
};

export default function ClientsPage() {
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [looking, setLooking] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();
  const [reps, setReps] = useState([]);

  useEffect(() => { api.get("/representatives").then((r) => setReps(r.data)).catch(() => {}); }, []);

  const selectVendedor = (repId) => {
    const rep = reps.find((r) => r.id === repId);
    setForm((f) => ({ ...f, vendedor_id: repId, vendedor: rep ? rep.name : "", vendedor_email: rep ? rep.email : "", vendedor_codigo: rep ? (rep.codigo || "") : "" }));
  };

  const load = useCallback(async () => {
    setClients((await api.get("/clients", { params: q ? { q } : {} })).data);
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const doImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importação concluída: ${data.created} novos, ${data.updated} atualizados, ${data.skipped} ignorados.`);
      setImportOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c, endereco: { ...empty.endereco, ...c.endereco }, endereco_entrega: { ...empty.endereco_entrega, ...(c.endereco_entrega || {}) } }); setOpen(true); };

  const lookup = async () => {
    const digits = (form.cnpj || "").replace(/\D/g, "");
    if (digits.length !== 14) { toast.error("CNPJ deve ter 14 dígitos."); return; }
    setLooking(true);
    try {
      const { data } = await api.get(`/cnpj/${digits}`);
      setForm((f) => ({ ...f, cnpj: data.cnpj, razao_social: data.razao_social, nome_fantasia: data.nome_fantasia, endereco: { ...f.endereco, ...data.endereco }, email: data.email || f.email, telefone: data.telefone || f.telefone }));
      toast.success("Dados encontrados.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLooking(false); }
  };

  const save = async () => {
    try {
      const payload = { ...form };
      if (!payload.usa_endereco_entrega) payload.endereco_entrega = null;
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success("Cliente salvo.");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir ${c.razao_social}?`)) return;
    await api.delete(`/clients/${c.id}`); toast.success("Cliente excluído."); load();
  };

  const setEnd = (field, val) => setForm((f) => ({ ...f, endereco: { ...f.endereco, [field]: val } }));

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Clientes</h1>
          <p className="text-muted-foreground mt-1">Cadastro com busca automática por CNPJ.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="import-clients-btn"><UploadSimple size={18} weight="bold" /> Importar Excel</Button>
          <Button onClick={openNew} className="bg-accent hover:bg-accent/90 text-white gap-2" data-testid="new-client-btn"><Plus size={18} weight="bold" /> Novo Cliente</Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar cliente..." className="pl-10" data-testid="clients-search" />
      </div>

      <Card className="shadow-none border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-zinc-50 border-b border-border">
              {["Razão Social", "CNPJ", "Cidade/UF", "Cadastro", "acoes"].map((h, i) => <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">{h === "acoes" ? "" : h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-border">
              {clients.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">Nenhum cliente cadastrado.</td></tr>}
              {clients.map((c) => (
                <tr key={c.id} className="hover:bg-zinc-50" data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-3"><p className="font-medium text-zinc-900">{c.razao_social}</p><p className="text-xs text-zinc-500">{c.nome_fantasia}</p></td>
                  <td className="px-4 py-3 font-mono text-zinc-600">{formatCNPJ(c.cnpj)}</td>
                  <td className="px-4 py-3 text-zinc-600">{c.endereco?.municipio}{c.endereco?.uf ? `/${c.endereco.uf}` : ""}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={c.cadastro_completo ? "border-accent text-accent" : "border-amber-500 text-amber-600"}>{c.cadastro_completo ? "Completo" : "Incompleto"}</Badge></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(c)} className="text-zinc-400 hover:text-accent mr-3" data-testid={`edit-client-${c.id}`}><PencilSimple size={18} /></button>
                    {isAdmin && <button onClick={() => remove(c)} className="text-zinc-400 hover:text-destructive"><Trash size={18} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar cliente" : "Novo cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1 space-y-1"><Label className="text-xs uppercase text-zinc-500">CNPJ</Label><Input value={formatCNPJ(form.cnpj)} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} data-testid="client-cnpj" /></div>
              <Button onClick={lookup} disabled={looking} className="bg-zinc-950 text-white gap-2 self-end" data-testid="client-cnpj-lookup"><CloudArrowDown size={18} /> {looking ? "..." : "Buscar"}</Button>
            </div>
            <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Razão Social</Label><Input value={form.razao_social} onChange={(e) => setForm({ ...form, razao_social: e.target.value })} data-testid="client-razao" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Nome Fantasia</Label><Input value={form.nome_fantasia} onChange={(e) => setForm({ ...form, nome_fantasia: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Inscrição Estadual</Label><Input value={form.ie} onChange={(e) => setForm({ ...form, ie: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase text-zinc-500">Vendedor</Label>
                <Select value={form.vendedor_id || undefined} onValueChange={selectVendedor}>
                  <SelectTrigger data-testid="client-vendedor"><SelectValue placeholder="Selecione o vendedor" /></SelectTrigger>
                  <SelectContent>
                    {reps.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase text-zinc-500">E-mail do vendedor</Label>
                <Input value={form.vendedor_email} readOnly placeholder="Preenchido automaticamente" className="bg-zinc-50" data-testid="client-vendedor-email" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1 col-span-2"><Label className="text-xs uppercase text-zinc-500">Logradouro</Label><Input value={form.endereco.logradouro} onChange={(e) => setEnd("logradouro", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Número</Label><Input value={form.endereco.numero} onChange={(e) => setEnd("numero", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Bairro</Label><Input value={form.endereco.bairro} onChange={(e) => setEnd("bairro", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Município</Label><Input value={form.endereco.municipio} onChange={(e) => setEnd("municipio", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">UF</Label><Input value={form.endereco.uf} onChange={(e) => setEnd("uf", e.target.value)} maxLength={2} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">CEP</Label><Input value={form.endereco.cep} onChange={(e) => setEnd("cep", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Suframa</Label><Input value={form.suframa} onChange={(e) => setForm({ ...form, suframa: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Contato</Label><Input value={form.contato} onChange={(e) => setForm({ ...form, contato: e.target.value })} data-testid="client-contato" /></div>
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">E-mail</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Celular</Label><Input value={form.celular} onChange={(e) => setForm({ ...form, celular: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Telefone</Label><Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-border">
              <Switch checked={form.usa_endereco_entrega} onCheckedChange={(v) => setForm({ ...form, usa_endereco_entrega: v })} data-testid="toggle-delivery" />
              <Label className="text-sm">Endereço de entrega diferente</Label>
            </div>
            {form.usa_endereco_entrega && (
              <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-50 rounded-md">
                <div className="space-y-1 col-span-2"><Label className="text-xs uppercase text-zinc-500">Logradouro (entrega)</Label><Input value={form.endereco_entrega.logradouro} onChange={(e) => setForm({ ...form, endereco_entrega: { ...form.endereco_entrega, logradouro: e.target.value } })} /></div>
                <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">Município</Label><Input value={form.endereco_entrega.municipio} onChange={(e) => setForm({ ...form, endereco_entrega: { ...form.endereco_entrega, municipio: e.target.value } })} /></div>
                <div className="space-y-1"><Label className="text-xs uppercase text-zinc-500">UF</Label><Input value={form.endereco_entrega.uf} onChange={(e) => setForm({ ...form, endereco_entrega: { ...form.endereco_entrega, uf: e.target.value } })} maxLength={2} /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-accent hover:bg-accent/90 text-white" data-testid="save-client-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Importar clientes (Excel)</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Colunas reconhecidas: <b>NOME</b>, <b>FANTASIA</b>, <b>VENDEDOR</b>, <b>ENDEREÇO</b>, <b>NUMERO</b>, <b>COMPLEMENTO</b>, <b>BAIRRO</b>, <b>CIDADE</b>, <b>UF</b>, <b>CEP</b>, <b>TELEFONES</b>, <b>CPF/CNPJ</b>, <b>INSCRIÇÃO</b>, <b>EMAIL</b>. Clientes existentes (mesmo CNPJ) são atualizados.
          </p>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={doImport} disabled={importing} className="text-sm" data-testid="import-clients-file" />
          {importing && <p className="text-sm text-accent">Importando, aguarde...</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
